import qs from "qs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useHistory } from "react-router-dom";

import StaffFilter from "~/pages/Staff/ui/StaffFilter/StaffFilter";
import Wrapper from "~/shared/ui/Wrapper";
import { checkRole } from "~/hooks/checkRole";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import { filterByArr } from "~/pages/Staff/assets/StaffFilter/lib";
import { getStaffPositions } from "~/pages/Staff/lib/utils";
import { StaffControl } from "~/pages/Staff/ui/StaffControl/StaffTrainingControl";
import {
  TDealerMetric,
  TStaffFilter,
} from "~/pages/Staff/ui/StaffFilter/model/config";
import { TTrainingFilter } from "~/pages/StaffTraining/ui/StaffTrainingFilter/model/config";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useNotify } from "~/shared/hooks/useNotify";
import { TableV2 } from "~/shared/ui/TableV2";

import StaffModal from "./StaffModal";
import { getDataStaff } from "./GetDataStaffRequests/getDataStaff";
import { getDataStaffReport } from "./GetDataStaffRequests/getDataStaffReport";
import { getStaffLicensesValue } from "./lib/formatLicensePair";
import { getSpecStaffColumns, getStaffColumns } from "./model/tableAdapter";
import { SPEC_STAFF_OPTIONS, STAFF_OPTIONS } from "./options";
import { toServer } from "./ui/StaffFilter/model/adapter";
import { StaffSummary } from "./ui/StaffSummary";

import s from "./Staff.less";

const language = localStorage.getItem("language");
const isRu = language === "ru" || language === null;

export const defaultStaffFilterValues = () => {
  return {
    filterBy: {
      ...filterByArr[0],
      label: isRu ? filterByArr[0].label : filterByArr[0].labelEn,
    },
    filterItems: [
      {
        value: sessionStorage.getItem("currentDealerId"),
        label:
          (isRu
            ? sessionStorage.getItem("currentDealerName")
            : sessionStorage.getItem("currentDealerNameEn")) ||
          (isRu ? "Не определен" : "Not defined"),
      },
    ],
    period: new Date(),
  };
};

const Staff = () => {
  const { t } = useTranslation();

  const [filter, setFilter] = useState(false);
  const [urlParams, setUrlParams] = useState<any>();
  const [dataStaffs, setDataStaffs] = useState();
  const [subtotals, setSubtotals] = useState<any>();
  const [specStaffsState, setSpecStaffsState] = useState([]);
  const [dealerMetric, setDealerMetric] = useState<TDealerMetric>(null);
  const [queryEnabled, setQueryEnabled] = useState(false);
  const [modal, setModal] = useState(false);
  const [currentDate, setCurrentDate] = useState();
  const params = new URLSearchParams();
  const settedFilter = useURLParamsGetter<TStaffFilter>();

  const setQuerySearch = useURLParamsSetter();
  const onSubmit = (value: TTrainingFilter) => {
    setQuerySearch(value);
  };
  const debouncedRef = useRef(null);

  useEffect(() => {
    clearTimeout(debouncedRef.current);
    if (!!Object.keys(settedFilter).length) {
      debouncedRef.current = setTimeout(() => {
        const params = qs.stringify(toServer(settedFilter));
        setUrlParams(params.toString());
      }, 0);
    }
  }, [settedFilter]);

  const {
    data: queryData,
    error,
    isLoading,
  } = useQuery({
    queryKey: ["getStaff", urlParams],
    queryFn: () => getDataStaff(urlParams),
  });

  const {
    data: queryDataReport,
    error: errorReport,
    isLoading: isLoadingReport,
  } = useQuery({
    queryKey: ["getStaffReport", urlParams],
    queryFn: () => getDataStaffReport(urlParams),
    enabled: queryEnabled,
    staleTime: Infinity,
  });

  const { notifyError, notifyReport } = useNotify();

  useEffect(() => {
    if (queryDataReport) {
      notifyReport(queryDataReport.fileHash);
    }
  }, [queryDataReport]);
  const clickBtn = () => {
    getDataStaff(params.toString());
  };

  const errorNotification = () => {
    notifyError(t("common.data_uploaded_error_size"));
  };

  useEffect(() => {
    if (!!queryData && queryData?.metrics) {
      setDealerMetric(queryData.metrics);
    }
  }, [queryData]);

  useEffect(() => {
    if (error || errorReport) {
      return errorNotification();
    }
  }, [error, errorReport]);

  const checkValuesPerson = (person, numbers, isFutureStandard = false) => {
    if (isFutureStandard && numbers === 0) {
      return "";
    }
    return person ? (numbers ? numbers : numbers === 0 ? "0" : " - ") : " - ";
  };

  const checkValuesPercent = (person, numbers) =>
    person
      ? numbers
        ? numbers.toFixed(1) + "%"
        : numbers === 0
          ? " - "
          : " - "
      : " - ";

  useEffect(() => {
    if (queryData) {
      const { currentDate } = queryData?.sum?.number;
      currentDate && setCurrentDate(currentDate);
      setSpecStaffsState(
        queryData.lines.map((item: any) => {
          const { combineCertification, combineTraining } = item.expectation;
          return {
            // --- Общее ---
            ...item,
            kpi: combineCertification || combineTraining,
            id: item.expectation.instanceId,
            Positions: item.expectation.positions,
            combineCertification,
            combineTraining,
            code: item.expectation.positions.map((c) => c.code).join(", "),
            position: getStaffPositions(item.expectation.positions, language),
            fromDate: item.statistics.number.fromDate,
            // --- Численность ---
            standardNumber: checkValuesPerson(
              item.statistics.number,
              item.statistics.number.standard,
            ),
            factNumber: checkValuesPerson(
              item.statistics.number,
              item.statistics.number.fact,
            ),
            futureNumber: checkValuesPerson(
              item.statistics.number,
              item.statistics.number.futureStandard,
              true,
            ),
            // --- Обучение ---
            personRequiredTraining: checkValuesPerson(
              item.statistics.training?.required,
              item.statistics.training?.required.fact,
            ),
            percentRequiredTraining: checkValuesPercent(
              item.statistics.training.required,
              item.statistics.training.required.percent,
            ),
            personTrained: checkValuesPerson(
              item.statistics.training?.trained,
              item.statistics.training?.trained.fact,
            ),
            percentTrained: checkValuesPercent(
              item.statistics.training?.trained,
              item.statistics.training?.trained.percent,
            ),
            // --- Сертификация ---
            personRequiredCertificated: checkValuesPerson(
              item.statistics.certification.required,
              item.statistics.certification.required.people,
            ),
            percentRequiredCertificated: checkValuesPercent(
              item.statistics.certification.required,
              item.statistics.certification.required.percent,
            ),
            personCertificated: checkValuesPerson(
              item.statistics.certification.certificated,
              item.statistics.certification.certificated.people,
            ),
            percentCertificated: checkValuesPercent(
              item.statistics.certification.certificated,
              item.statistics.certification.certificated.percent,
            ),
            licensesIssued: getStaffLicensesValue(
              item.statistics.licenses,
              "issued",
            ),
            licensesRemainder: getStaffLicensesValue(
              item.statistics.licenses,
              "remainder",
            ),
          };
        }),
      );
    }
  }, [queryData]);

  useEffect(() => {
    if (queryData) {
      setDataStaffs(() => {
        return queryData.lines.map((item: any) => {
          return {
            id: item.expectation.instanceId,
            Positions: item.expectation.positions,
            position: item.expectation.positions
              .map((n) =>
                language === "ru" ? n.name || n.nameEn : n.nameEn || n.name,
              )
              .join(", "),
            isCombining: item.expectation.isCombining ? "+" : "-",
            method: isRu
              ? item.expectation.method || item.expectation.methodEn
              : item.expectation.methodEn || item.expectation.method,
            code: item.expectation.positions.map((c) => c.code).join(", "),
          };
        });
      });
    }
  }, [queryData]);

  useEffect(() => {
    if (queryData) {
      setSubtotals(queryData.sum);
    }
  }, [queryData]);

  const getStaffReports = () => {
    setQueryEnabled(true);
  };

  const isValuesNotLoaded = specStaffsState.some((staff) => {
    const staffDate = new Date(staff.fromDate);
    return (
      staffDate.getTime() <= new Date().getTime() && !isNaN(staffDate.getTime())
    );
  });

  const getMaxFromDate = (specStaffsState) => {
    const dates = specStaffsState.map((staff) => {
      if (!staff.fromDate || staff.fromDate === "") {
        return null;
      }
      return new Date(staff.fromDate);
    });

    const maxDate = dates.reduce((max, current) => {
      if (current && (!max || current > max)) {
        return current;
      }
      return max;
    }, null);

    return maxDate ? maxDate : "";
  };

  const maxFromDate = getMaxFromDate(specStaffsState);

  const handleModal = () => {
    setModal((state) => !state);
  };

  const history = useHistory();
  const goStaffBonus = () => {
    history.replace("/staff/bonus");
  };

  const goStaffTraining = () => {
    history.replace("/staff/training");
  };
  const specStaffColumns = getSpecStaffColumns(
    t,
    SPEC_STAFF_OPTIONS,
    maxFromDate,
    dealerMetric,
    currentDate,
  );
  const staffColumns = getStaffColumns(t, STAFF_OPTIONS);

  return (
    <Wrapper.Content
      title={t("staff.staffSubTitle")}
      filterComponent={
        <div className={s.staffPageFilterArea}>
          <div className={s.staffToolbarAboveFilter}>
            <StaffControl
              getStaffReports={getStaffReports}
              handleModal={handleModal}
              goStaffBonus={goStaffBonus}
              goStaffTraining={goStaffTraining}
            />
          </div>
          <StaffFilter onSubmit={onSubmit} />
        </div>
      }
    >
      <div className={s.wrapper}>
        <TableV2
          columns={specStaffColumns}
          dataSource={specStaffsState}
          loading={isLoading}
          wrapperClassName={s.staff_spec_table}
          pagination={false}
          {...(!!subtotals
            ? {
                summary: () => (
                  <StaffSummary
                    number={subtotals?.number}
                    training={subtotals?.training}
                    certification={subtotals?.certification}
                    /* TODO PASDILER-2428: итоговые лицензии временно скрыты до завершения работ в feat/PASDILER-1829. https://task.sotl.group:8443/browse/PASDILER-2428
                    licenses={mapStatisticsLicenses(queryData?.licenses)} */
                  />
                ),
              }
            : null)}
        />
        {/* <Table
          options={SPEC_STAFF_OPTIONS.map((option) => {
            if (option === "futureNumber") {
              const fromDateParam = maxFromDate
                ? `${getFormatDate(maxFromDate, true)},`
                : "";
              return {
                key: option,
                handle: () =>
                  t("staff.futureNumber", { fromDate: fromDateParam }),
              };
            }
            if (option === "standardNumber") {
              return {
                key: option,
                handle: () =>
                  t("staff.standardNumber", {
                    date: getFormatDate(currentDate),
                  }),
              };
            }

            return option;
          })}
          items={specStaffsState}
          loading={isLoading}
          type={"staff"}
          striped
          sortable={false}
          customClassName={s.staff_spec_table}
          customTableHeaderClass={s.headerBorder}
          // role={role}
          subtotals={subtotals}
          HeaderComponent={(props) => (
            <StaffTableHeader
              {...props}
              dealerMetric={dealerMetric}
              isValuesNotLoaded={isValuesNotLoaded}
            />
          )}
          FooterComponent={StaffTableFooter}
          headerBorderPerElementsCount={[2, 5, 9]}
        /> */}
        <TableV2
          columns={staffColumns}
          dataSource={dataStaffs}
          loading={isLoading}
          wrapperClassName={s.staff_table}
          pagination={false}
        />
        {/* <Table
          options={STAFF_OPTIONS}
          items={dataStaffs}
          loading={isLoading}
          type={"staff"}
          striped
          sortable={false}
          customClassName={s.staff_table}
        /> */}
        <StaffModal
          handleModal={handleModal}
          modal={modal}
          queryData={queryData}
          urlParams={urlParams}
        />
      </div>
    </Wrapper.Content>
  );
};

export default Staff;
